/**
 * useMarkPrice Hook
 *
 * Returns mark price data for a single symbol from Binance Futures.
 * Automatically filters out stale data (>30s old).
 */

import { useFuturesData } from '../contexts/FuturesDataContext';
import type { MarkPriceData } from '../services/futures/types';

/**
 * Get mark price data for a specific symbol
 *
 * @param symbol - Unified symbol format (e.g., "BTC/USDT")
 * @returns MarkPriceData or undefined if not available or stale
 *
 * @example
 * ```tsx
 * const markPrice = useMarkPrice('BTC/USDT');
 * if (markPrice) {
 *   console.log('Mark Price:', markPrice.markPrice);
 *   console.log('Funding Rate:', markPrice.fundingRate);
 * }
 * ```
 */
export const useMarkPrice = (symbol: string): MarkPriceData | undefined => {
  const { markPrices } = useFuturesData();

  // Null-safety: Check if symbol exists in map
  if (!symbol || !markPrices.has(symbol)) {
    return undefined;
  }

  // Return mark price data
  // Note: staleness filtering already done by context (via getAllMarkPrices)
  return markPrices.get(symbol);
};
